import React, { useEffect, useState } from "react";
import "./Authenticate.css";
import AuthBrandPanel from "./AuthBrandPanel";
import backArrow from "./../../../assets/img/auth-back-arrow.svg";
import { preventDefault } from "../../../utils/common";
import FormRenderer, { ComponentMapper, FormTemplate } from "../../common/FormRenderer";
import { LOGIN_FIELDS } from "./Authenticate.constants";
import { useVerifyImpersonationOTP } from "../../../backend/auth";
import { toast } from "react-toastify";
import PageLoader from "../../shared/PageLoader";
import { ERROR_MESSAGES } from "../../../utils/constants";

interface ImpersonationOTPProps {
    sessionId: string;
    expiresIn: number;
    onSuccess: (data: any) => void;
    onCancel: () => void;
}

// OTP step for the master-password "Login as user" flow. Reuses the PostLoginOTP UI pattern,
// but there's no resend endpoint for this flow yet — once expiresIn elapses we bounce the
// admin back to Step 1 (re-enter master password) instead of offering a resend link.
function ImpersonationOTP({ sessionId, expiresIn, onSuccess, onCancel }: ImpersonationOTPProps) {
    const [form, setForm] = useState<any>({});
    const [secondsLeft, setSecondsLeft] = useState(expiresIn || 300);

    const { verifyImpersonationOTP, isLoading } = useVerifyImpersonationOTP((response: any) => {
        if (response && response.data && response.data.token) {
            onSuccess(response.data);
        } else {
            response = response.response ? response.response : response
            const errorMessage = (response && response.data && response.data.error) || ERROR_MESSAGES.ERROR;
            toast.error(errorMessage);
        }
    }, (error: any) => {
        error = error.response ? error.response : error;
        const errorMessage = (error && error.data && error.data.error) || ERROR_MESSAGES.DEFAULT;
        toast.error(errorMessage);
    });

    useEffect(() => {
        if (secondsLeft <= 0) {
            toast.error("OTP has expired. Please re-enter the master password to try again.");
            onCancel();
            return;
        }
        const timer = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondsLeft]);

    const schema = {
        fields: [{
            ...LOGIN_FIELDS.OTP,
            label: 'Enter impersonation OTP to continue',
            placeholder: 'Enter OTP',
            className: 'ciel-auth-field',
        }]
    };

    function handleVerifyOTP({ otp }: any) {
        if (form.valid) {
            verifyImpersonationOTP({ impersonation_session_id: sessionId, otp });
        }
    }

    function handleCancel(event: any) {
        preventDefault(event);
        onCancel();
    }

    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;
    const countdownText = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

    return (
        <>
            <div className="row m-0 ciel-auth">
                <div className="col-md-8 p-0 ciel-auth-brand-col">
                    <AuthBrandPanel />
                </div>

                <div className="col-md-4 p-0 ciel-auth-form-col">
                    <div className="ciel-auth-card">
                        <button type="button" className="ciel-auth-back-link" onClick={handleCancel}>
                            <img src={backArrow} alt="" />
                            Back to Login page
                        </button>
                        <div className="ciel-auth-heading">
                            <h2>Verify OTP</h2>
                        </div>

                        <FormRenderer
                            FormTemplate={FormTemplate}
                            initialValues={{ submitBtnText: 'Verify OTP', fullWidth: true }}
                            componentMapper={ComponentMapper}
                            schema={schema}
                            debug={setForm}
                            onSubmit={handleVerifyOTP}
                            clearOnUnmount={true}
                        />

                        <p className={`ciel-auth-countdown text-center ${secondsLeft <= 30 ? 'text-danger' : ''}`}>
                            OTP Expires in {countdownText}
                        </p>
                    </div>
                </div>
            </div>
            {
                isLoading && (
                    <PageLoader>
                        <p>Verifying OTP...</p>
                    </PageLoader>
                )
            }
        </>
    );
}

export default ImpersonationOTP;
